import React, { useEffect, useState } from 'react'
import { Dialog } from '@headlessui/react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface NotesModalProps {
  isOpen: boolean
  onClose: () => void
  initialNote?: string
  onSave?: (note: string) => void
  isRTL?: boolean
}

const NotesModal: React.FC<NotesModalProps> = ({
  isOpen,
  onClose,
  initialNote = '',
  onSave,
  isRTL = true,
}) => {
  const { t } = useTranslation()
  const [note, setNote] = useState(initialNote)

  useEffect(() => {
    if (isOpen) setNote(initialNote)
  }, [isOpen, initialNote])

  const handleSave = () => {
    onSave?.(note.trim())
    onClose()
  }

  const handleBackdrop = () => onClose()

  return (
    <Dialog open={isOpen} onClose={handleBackdrop} className="relative z-10">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          className={`w-full max-w-[480px] bg-white rounded-3xl overflow-hidden shadow-2xl ${
            isRTL ? 'text-right' : 'text-left'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-gray-100">
              <Dialog.Title className="flex-1 text-xl font-medium text-flutter-black font-['IBM Plex Sans Arabic']">
              {t('common.notes')}
            </Dialog.Title>
            <button
              onClick={onClose}
              className={`w-9 h-9 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center ${
                isRTL ? '' : ''
              }`}
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          
          </div>

          {/* Content */}
          <div className="px-4 pt-2 pb-4 space-y-4">
            <div className="h-36 w-full relative">
              <textarea
                value={note}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.length <= 265) {
                    setNote(value)
                  }
                }}
                maxLength={265}
                placeholder={t('common.notes')}
                className={`w-full h-full resize-none bg-white rounded-xl p-4 text-sm leading-5 text-flutter-black placeholder-gray-400 outline-none border-0 focus:ring-0 focus:outline-none ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              />
              <div className={`absolute bottom-2 ${isRTL ? 'left-4' : 'right-4'} text-xs text-gray-400`}>
                {note.length}/265
              </div>
            </div>

            <button
              onClick={handleSave}
              className="w-full h-12 px-6 bg-flutter-button rounded-xl text-white font-semibold font-['IBM Plex Sans Arabic'] hover:bg-orange-600 active:scale-[0.99] transition-all"
            >
              {t('common.save')}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default NotesModal
